// SwipeCard.jsx
// Reusable swipe gesture engine — powers ALL swipe flows
// Swipe RIGHT  → onSwipeRight (approve / redeem / confirm)
// Swipe LEFT   → onSwipeLeft  (reject)
// Swipe DOWN   → onSwipeDown  (partial approval)

import React, { useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";

const SWIPE_THRESHOLD   = 80;   // px to trigger action
const PARTIAL_THRESHOLD = 60;   // px downward to trigger partial

const SwipeCard = ({
    children,
    onSwipeRight,
    onSwipeLeft,
    onSwipeDown,
    disabled = false,
    rightLabel  = "✅ Approve",
    leftLabel   = "❌ Reject",
    downLabel   = "⚡ Partial",
    rightColor  = "#22c55e",
    leftColor   = "#ef4444",
    downColor   = "#f59e0b",
    showHints   = true,
}) => {
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const [direction, setDirection] = useState(null); // "right" | "left" | "down"
    const [triggered, setTriggered]  = useState(false);

    // Opacity of hint overlays based on drag
    const rightOpacity = useTransform(x, [0, SWIPE_THRESHOLD],     [0, 1]);
    const leftOpacity  = useTransform(x, [-SWIPE_THRESHOLD, 0],    [1, 0]);
    const downOpacity  = useTransform(y, [0, PARTIAL_THRESHOLD],   [0, 1]);

    // Subtle rotation while dragging
    const rotate = useTransform(x, [-150, 150], [-8, 8]);

    // Card background tint
    const background = useTransform(
        x,
        [-SWIPE_THRESHOLD, 0, SWIPE_THRESHOLD],
        [`${leftColor}22`, "transparent", `${rightColor}22`]
    );

    const handleDragEnd = async (_, info) => {
        if (disabled || triggered) return;

        const { offset } = info;

        const isRight  = offset.x >  SWIPE_THRESHOLD;
        const isLeft   = offset.x < -SWIPE_THRESHOLD;
        const isDown   = offset.y >  PARTIAL_THRESHOLD && Math.abs(offset.x) < 40;

        if (isRight && onSwipeRight) {
            setTriggered(true);
            setDirection("right");
            await animate(x, 400, { duration: 0.3 });
            onSwipeRight();
        } else if (isLeft && onSwipeLeft) {
            setTriggered(true);
            setDirection("left");
            await animate(x, -400, { duration: 0.3 });
            onSwipeLeft();
        } else if (isDown && onSwipeDown) {
            setTriggered(true);
            setDirection("down");
            await animate(y, 200, { duration: 0.3 });
            onSwipeDown();
        } else {
            // Snap back
            animate(x, 0, { type: "spring", stiffness: 300, damping: 25 });
            animate(y, 0, { type: "spring", stiffness: 300, damping: 25 });
            setDirection(null);
        }
    };

    const handleDrag = (_, info) => {
        const { offset } = info;
        if (Math.abs(offset.x) > Math.abs(offset.y)) {
            setDirection(offset.x > 0 ? "right" : "left");
        } else if (offset.y > 20) {
            setDirection("down");
        } else {
            setDirection(null);
        }
    };

    return (
        <div className="relative w-full select-none touch-none">
            {showHints && (
                <>
                    {onSwipeRight && (
                        <motion.div
                            style={{ opacity: rightOpacity }}
                            className="absolute inset-0 z-10 flex items-center justify-start pl-4 pointer-events-none rounded-xl"
                        >
                            <div
                                className="text-sm font-black px-3 py-1 rounded-lg border-2"
                                style={{ color: rightColor, borderColor: rightColor, background: `${rightColor}22` }}
                            >
                                {rightLabel}
                            </div>
                        </motion.div>
                    )}

                    {onSwipeLeft && (
                        <motion.div
                            style={{ opacity: leftOpacity }}
                            className="absolute inset-0 z-10 flex items-center justify-end pr-4 pointer-events-none rounded-xl"
                        >
                            <div
                                className="text-sm font-black px-3 py-1 rounded-lg border-2"
                                style={{ color: leftColor, borderColor: leftColor, background: `${leftColor}22` }}
                            >
                                {leftLabel}
                            </div>
                        </motion.div>
                    )}

                    {onSwipeDown && (
                        <motion.div
                            style={{ opacity: downOpacity }}
                            className="absolute inset-0 z-10 flex items-end justify-center pb-2 pointer-events-none rounded-xl"
                        >
                            <div
                                className="text-sm font-black px-3 py-1 rounded-lg border-2"
                                style={{ color: downColor, borderColor: downColor, background: `${downColor}22` }}
                            >
                                {downLabel}
                            </div>
                        </motion.div>
                    )}
                </>
            )}

            {/* ── Draggable Card ── */}
            <motion.div
                drag={disabled ? false : true}
                dragConstraints={{ top: -20, bottom: 200, left: -300, right: 300 }}
                dragElastic={0.15}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                style={{ x, y, rotate, background }}
                className="relative z-20 w-full cursor-grab active:cursor-grabbing rounded-xl"
                whileTap={{ scale: disabled ? 1 : 0.98 }}
            >
                {children}
            </motion.div>

            {showHints && !triggered && (
                <div className="flex justify-between items-center mt-1 px-2 text-[10px] text-gray-600 select-none pointer-events-none">
                    {onSwipeLeft  && <span style={{ color: leftColor  }}>← {leftLabel}</span>}
                    {onSwipeDown  && <span style={{ color: downColor  }}>↓ {downLabel}</span>}
                    {onSwipeRight && <span style={{ color: rightColor }}>→ {rightLabel}</span>}
                </div>
            )}
        </div>
    );
};

export default SwipeCard;
